import { analytics } from 'shared/utils/analytics';
import { initLogContext } from 'shared/utils/logging-context';
import { history } from './reducers';
import { populateMediaDevices, resetDashboardState } from './dashboard-slice';
import { selectActiveUser } from './shared-selectors';
import { checkAccount, setAccount, setNoAccount } from './shared-actions';
import { mediaDevices } from 'shared/utils/media-devices';
import log from 'shared/utils/log';
import { addNativeMessageListener } from 'native/utils/nativemessage';
import type { Store } from './index';
import { getStoredAuthenticationToken } from './storage';
import { startTour } from './tour-slice';
import { TourStepId } from 'shared/utils/tour';
import { resetRealtimeState } from './realtime-slice';
import { resetAppNotificationState } from './app-notification-slice';
import { loadCustomProperties } from 'shared/store/org-slice';
import { initUiConfig } from 'shared/store/ui-config-slice';
import { initAiChat } from './ai-chat-slice';

/**
 * Universal behaviors to initialize the store with data. Dispatch actions and
 * scrape server-side embedded data from the DOM.
 */
export function initStore(store: Store, skip?: boolean) {
  initLogContext(store);
  initAccount(store, skip);
  initMediaDevices(store);
  initIdentityWatcher(store);
  initHistoryWatcher();
  initMessageHandlers();
  initTour(store);
  initAiChat(store);
  if (!skip) {
    initProperties(store);
    initUiConfig(store);
  }
}

/**
 * Initialize fetching of account data either by API or embedded in DOM.
 */
function initAccount(store: Store, skip?: boolean) {
  if (skip) {
    return store.dispatch(setNoAccount());
  }

  const accountJsonEl = document.getElementById('__sb_account_json__');
  if (!accountJsonEl) {
    return store.dispatch(checkAccount());
  }
  try {
    const accountJson = JSON.parse(accountJsonEl.innerHTML);
    if (accountJson) {
      return store.dispatch(setAccount(accountJson));
    } else if (accountJson === false) {
      // Only truly assume no account if they also don't have a LS token,
      // otherwise we'll let them check account with that too.
      if (!getStoredAuthenticationToken()) {
        return store.dispatch(setNoAccount());
      }
    } else {
      throw new Error(`Unexpected account JSON: ${JSON.stringify(accountJson)}`);
    }
  } catch (err) {
    log.error('Encountered error while parsing prefetched account JSON', undefined, err);
  }
  return store.dispatch(checkAccount());
}

function initProperties(store: Store) {
  return store.dispatch(loadCustomProperties());
}

/**
 * Initialize media devices, update on changes.
 */
function initMediaDevices(store: Store) {
  store.dispatch(populateMediaDevices());
  mediaDevices.listenToDeviceChange(() => store.dispatch(populateMediaDevices()));
}

/**
 * Initializes a watcher on user info changes, updates integrations accordingly.
 */
function initIdentityWatcher(store: Store) {
  let curAuthed = selectActiveUser(store.getState());
  store.subscribe(() => {
    const prevAuthed = curAuthed;
    curAuthed = selectActiveUser(store.getState());
    const loggedIn = !prevAuthed && curAuthed;
    const loggedOut = prevAuthed && !curAuthed;
    const changedUser = prevAuthed && curAuthed && prevAuthed.id !== curAuthed.id;

    if (loggedOut || changedUser) {
      store.dispatch(resetDashboardState());
      store.dispatch(resetRealtimeState());
      store.dispatch(resetAppNotificationState());
    }
    if (curAuthed && (loggedIn || changedUser)) {
      // Identify the user to analytics
      analytics.identify(curAuthed.id.toString(), {
        name: curAuthed.displayName,
      });
      if ('segment' in analytics.plugins) {
        (analytics.plugins as any).segment.group(curAuthed.org.id);
      }
    }
  });
}

/**
 * Initializes history navigation watcher to report to analytics.
 */
function initHistoryWatcher() {
  // Do things on history navigation
  let prevPath: string | undefined;
  history.listen(() => {
    if (location.pathname !== prevPath) {
      prevPath = location.pathname;
      analytics.page();
    }
  });
}

/**
 * Initialize react-native and serviceworker message handling.
 */
function initMessageHandlers() {
  // route history on native message / service worker events
  const handleMessage = (rawData: any, source: string) => {
    try {
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      if (!data || data.type !== 'navigate') {
        return;
      }
      log.info(`Navigating to '${data.route}' from ${source} navigate event`);
      history.push(data.route);
    } catch (err) {
      log.error(`Error encountered handling ${source} message`, { rawData }, err);
    }
  };

  // Listen to native & serviceworker messages
  addNativeMessageListener(data => handleMessage(data, 'native message'));

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', ev => {
      handleMessage(ev.data, 'service worker');
    });
  }
}

/**
 * Initialize the tour, if we have the query param.
 */
function initTour(store: Store) {
  const params = new URLSearchParams(window.location.search);
  if (params.get('tourStep')) {
    store.dispatch(startTour((params.get('tourStep') as TourStepId) || undefined));
  }
}
